/**
 * PostHog analytics — graceful no-op when env key is absent.
 * Respects cookie consent: only tracks when user has accepted "all".
 *
 * Usage:
 *   import { analytics } from "../lib/analytics";
 *   analytics.track("quiz_started", { mode: "practice", domain: "people" });
 */
import posthog from "posthog-js";

const COOKIE_STORAGE_KEY = "pmpintel_cookie_consent_v1";
const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

let initialised = false;

function consentAllowsTracking() {
  try {
    const raw = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!raw) return false;
    const { choice } = JSON.parse(raw);
    return choice === "all";
  } catch {
    return false;
  }
}

export function initAnalytics() {
  if (initialised || !KEY || typeof window === "undefined") return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: "identified_only",
      capture_pageview: false, // we'll do it manually per route
      autocapture: false,
      disable_session_recording: true,
      opt_out_capturing_by_default: !consentAllowsTracking(),
    });
    initialised = true;
  } catch (e) {
    console.warn("PostHog init failed:", e);
  }
}

export function enableTracking() {
  if (!initialised) return;
  try { posthog.opt_in_capturing(); } catch { /* noop */ }
}

export function disableTracking() {
  if (!initialised) return;
  try { posthog.opt_out_capturing(); } catch { /* noop */ }
}

export const analytics = {
  identify(userId, traits = {}) {
    if (!initialised) return;
    try { posthog.identify(userId, traits); } catch { /* noop */ }
  },
  track(event, props = {}) {
    if (!initialised) return;
    try { posthog.capture(event, props); } catch { /* noop */ }
  },
  page(name, props = {}) {
    if (!initialised) return;
    try {
      posthog.capture("$pageview", { $current_url: window.location.href, page: name, ...props });
    } catch { /* noop */ }
  },
  reset() {
    if (!initialised) return;
    try { posthog.reset(); } catch { /* noop */ }
  },
  isConfigured: () => !!KEY,
};
